import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { Icon, Icons } from '@ge/components/icon';
import { View } from '@ge/models/constants';
import { useIsMobile } from '@ge/shared/data-hooks';
import { globalColors } from '@ge/tokens';

import { ViewSelectorStatus } from '../view-selector/view-selector-status';

import GEVernovaLogo from './images/GE-VRNV_Standard_RGB.svg';
import MOBILE_VIEW from './images/mobile.svg';
import PrivacyPolicyDialog from './models/privacy-policy';
import Navigation from './navigation';
import Profile from './profile';
import SitesAssetsCount from './sites-assets-count';
import ThemeToggle from './theme-toggle';


const Container = styled.div`
  height: 40px;
  background: ${(props) => props.theme.navigation.backgroundColor};
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const LogoLink = styled.a`
  padding: 0 12px;
  border-right: 1px solid ${(props) => props.theme.navigation.borderColor};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  img {
    border: none;
    outline: none;
  }
  &:focus {
    outline: none;
  }
`;


const MainNavContent = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

const LogoHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  background-color: ${(props) => props.theme.profileNav.borderTopColor};
`;

const StyledButton = styled.button`
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
`;

const StyledPrivacyButton = styled(Button)`
  white-space: nowrap;
  text-overflow: ellipsis;
  height: 24px;
  width: 210px;
  background:  ${({ theme }) => theme.createReport.widget.completedWork.techBadge.border};
  border: none;
`;

const Img = styled.img`
  width: 24px;
`;

const ImgBtn = styled.button`
  border-right: 1px solid ${(props) => props.theme.mainNav.sectionBorderColors};
`;

const MainNav = () => {
  let pathname = useLocation().pathname;
  const logoRef = useRef();
  const [previousPath, setPreviousPath] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const appView = localStorage.getItem('mobileAppView');
  const isMobile = useIsMobile();

  const handleToggle = useCallback(() => {
    setIsActive((prevState) => !prevState);
  }, []);

  const handleClose = useCallback(() => {
    setIsActive(false);
  }, []);

  const closePrivacyPolicyModal = useCallback(() => {
    setShowPrivacyModal(false);
  }, [setShowPrivacyModal]);

  const handleViewChange = () => {
    localStorage.setItem('mobileAppView', View.MOBILE);
    window.location.href = '/';
  }

  useEffect(() => {
    if (previousPath === pathname) {
      return;
    }

    setPreviousPath(pathname);

    // Take focus off of the link element the user just chose.
    // This way the menu will close since we have styles based
    // on focus within the <Navigation> element.
    return logoRef.current?.focus();
  }, [pathname, previousPath]);

  if (appView === View.MOBILE)
    return (
      <LogoHeader>
        <LogoLink href="/" tabIndex="0" ref={logoRef}>
          <img
            src={GEVernovaLogo}
            alt="GEVernovaLogo"
            style={{ height: 'auto', maxWidth: '140px' }}
          />
        </LogoLink>
        <MainNavContent>
          <ThemeToggle />
        </MainNavContent>
      </LogoHeader>
    )

  return (
    <>
      <LogoHeader>
        <LogoLink href="/" tabIndex="0" ref={logoRef}>
          <img
            src={GEVernovaLogo}
            alt="GEVernovaLogo"
            style={{ height: 'auto', maxWidth: '140px' }}
          />
        </LogoLink>
        {/* <h3 style={{ color: 'white' }}>{window.innerWidth}:{isMobile.toString()}:{isMobile ? View.MOBILE : View.DESKTOP}</h3> */}
        <StyledPrivacyButton type="button" onClick={() => setShowPrivacyModal(true)}>
          <span style={{ marginRight: '10px' }}>
            <Icon icon={Icons.EMAIL} size={11} color={globalColors.white} />
          </span>
          <span>{'Privacy Policy: Read More...'}</span>
        </StyledPrivacyButton>
        <MainNavContent>
          <ViewSelectorStatus
            isActive={isActive}
            handleClose={handleClose}
            handleToggle={handleToggle}
          />
          <StyledButton onClick={handleToggle}>
            <SitesAssetsCount />
          </StyledButton>
          {isMobile &&
            <ImgBtn type="button" onClick={handleViewChange}>
              <Img src={MOBILE_VIEW} alt="theme icon" />
            </ImgBtn>}
          <Profile />
        </MainNavContent>
      </LogoHeader>
      <Container>
        <Navigation previousPath={previousPath} />
      </Container>
      {showPrivacyModal && (
        <PrivacyPolicyDialog isOpen={showPrivacyModal} onClose={closePrivacyPolicyModal} />
      )}
    </>
  );
};

export default MainNav;
